import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Mail, Phone, Shield, Car, MapPin, FileText, AlertTriangle, Users, ChevronRight } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'

const API = process.env.REACT_APP_BACKEND_URL

export default function C2Clients() {
  const { profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const fetchedRef = useRef(false)

  const fetchClients = useCallback(async () => {
    if (!profile?.id) return
    
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      params.set('user_id', profile.id)
      params.set('role', profile.role)

      const res = await fetch(`${API}/api/c2/my-clients?${params}`)
      if (!res.ok) {
        throw new Error(`Erreur serveur (${res.status})`)
      }
      const data = await res.json()
      if (data.success) {
        setClients(data.clients || [])
      } else {
        setError(data.error || 'Erreur chargement clients')
      }
    } catch (e) {
      setError('Impossible de contacter le serveur. Veuillez rafraichir la page.')
    } finally {
      setLoading(false)
    }
  }, [profile?.id, profile?.role])

  useEffect(() => {
    // Wait for auth to finish loading AND profile to be available
    if (!authLoading && profile?.id && !fetchedRef.current) {
      fetchedRef.current = true
      fetchClients()
    } else if (!authLoading && !profile) {
      // Auth finished but no profile - stop loading
      setLoading(false)
    }
  }, [profile, authLoading, fetchClients])

  return (
    <div className="p-8" data-testid="c2-clients">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Clients
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {clients.length} client(s) dans votre portefeuille
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-12 justify-center">
          <div className="w-4 h-4 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin" />
          Chargement...
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center" data-testid="clients-error">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-600 font-medium">{error}</p>
          <button onClick={fetchClients} className="mt-3 text-xs text-red-500 hover:text-red-700 underline">
            Réessayer
          </button>
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center" data-testid="clients-empty">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600 mb-1">Aucun client dans votre portefeuille</p>
          <p className="text-xs text-slate-400 mb-4">
            {profile?.role === 'c2_gestionnaire'
              ? "Demandez à votre administrateur de vous assigner des clients."
              : "Créez un client depuis Configuration Clients."}
          </p>
          {(profile?.role === 'master_admin' || profile?.role === 'c2_admin') && (
            <button
              onClick={() => navigate('/admin/clients')}
              className="text-xs bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
              data-testid="go-config-clients"
            >
              Configuration Clients
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clients.map(t => (
            <div
              key={t.id}
              onClick={() => navigate(`/c2/clients/${t.id}`)}
              className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group"
              data-testid="client-card"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-md bg-indigo-50 flex items-center justify-center">
                    <Building2 size={16} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{t.nom}</p>
                    <p className="text-xs text-slate-400">{t.secteur_activite || t.siren || '—'}</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-500 transition-colors mt-1" />
              </div>

              {/* Compteurs rapides */}
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-1" title="Polices actives">
                  <Shield size={12} className="text-indigo-400" />
                  <span>{t.nb_polices || 0}</span>
                </div>
                <div className="flex items-center gap-1" title="Véhicules actifs">
                  <Car size={12} className="text-emerald-400" />
                  <span>{t.nb_vehicules || 0}</span>
                </div>
                <div 
                  className={`flex items-center gap-1 ${t.nb_sinistres_ouverts > 0 ? 'cursor-pointer hover:bg-amber-50 px-1.5 py-0.5 rounded -mx-1.5 -my-0.5' : ''}`}
                  title="Sinistres ouverts"
                  onClick={(e) => {
                    if (t.nb_sinistres_ouverts > 0) {
                      e.stopPropagation()
                      navigate(`/c2/sinistres?tenant_id=${t.id}`)
                    }
                  }}
                >
                  <FileText size={12} className={t.nb_sinistres_ouverts > 0 ? 'text-amber-500' : 'text-slate-400'} />
                  <span className={t.nb_sinistres_ouverts > 0 ? 'text-amber-600 font-medium' : ''}>{t.nb_sinistres_ouverts || 0}</span>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">
                  {t.statut}
                </span>
                {t.est_gestionnaire_principal && (
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                    Principal
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
